import React, { useCallback, useContext, useEffect, useState } from 'react';
import { z } from 'zod';

import Form from 'components/shared/Form';

import { ItemsContext } from 'components/shared/context/ItemsContextProvider';
import { ToastContext } from 'components/shared/context/ToastContextProvider';
import { UserContext } from 'components/shared/context/UserContextProvider';

import ValidatedBaseInput from 'components/shared/inputs/ValidatedBaseInput';

import BaseModal from 'components/shared/modals/BaseModal';

import sansanTech from 'services/sansantech';

import Item from 'schema/item';

interface EditItemModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  item: Item | null;
  resetItem: () => void;
}

const EditItemModal: React.FC<EditItemModalProps> = ({
  isOpen,
  setIsOpen,
  item,
  resetItem,
}) => {
  const { userState } = useContext(UserContext);
  const { toastDispatch } = useContext(ToastContext);
  const { setItems } = useContext(ItemsContext);

  const [submitting, setSubmitting] = useState(false);

  const [itemNameValue, setItemNameValue] = useState<string>(item?.name || '');
  const [isItemNameValueValid, setIsItemNameValueValid] =
    useState<boolean>(false);

  useEffect(() => {
    item?.name && setItemNameValue(item.name);
  }, [item]);

  const reset = useCallback(() => {
    setItemNameValue('');
    setIsItemNameValueValid(false);
    resetItem();
  }, [resetItem]);

  const onSubmit = useCallback(() => {
    setSubmitting(true);
    if (userState.token && item) {
      sansanTech
        .updateItem(userState.token, item.id, { name: itemNameValue })
        .then((response) => {
          console.log(response);
          setItems?.((currentItems) => {
            const updatedItem = response.data;
            const currentItemsCopy = [...currentItems];
            const itemIndex = currentItemsCopy.findIndex(
              (item) => item.id === updatedItem.id
            );
            currentItemsCopy[itemIndex] = updatedItem;
            return currentItemsCopy;
          });
          setSubmitting(false);
          toastDispatch?.({
            type: 'SUCCESS',
            message: 'Edited item',
          });
          setIsOpen(false);
          reset();
        })
        .catch((error) => {
          console.log(error);
          setSubmitting(false);
          toastDispatch?.({
            type: 'ERROR',
            message: 'Could not edit item',
          });
        });
    } else {
      setSubmitting(false);
      toastDispatch?.({ type: 'ERROR', message: 'No permission to do this' });
    }
  }, [
    itemNameValue,
    reset,
    toastDispatch,
    userState.token,
    setIsOpen,
    setItems,
    item,
  ]);

  return (
    <BaseModal
      isOpen={isOpen && Boolean(item)}
      setIsOpen={setIsOpen}
      header="Edit item"
      onClose={reset}
    >
      <Form
        header="Edit item"
        onSubmit={onSubmit}
        submitting={submitting}
        disabled={!isItemNameValueValid}
        submitButtonText="Edit item"
        submitButtonWidth="110px"
      >
        <ValidatedBaseInput
          label="item name"
          type="text"
          value={itemNameValue}
          setValue={setItemNameValue}
          setIsValid={setIsItemNameValueValid}
          validators={z.string().min(1, { message: 'This field is required' })}
        />
      </Form>
    </BaseModal>
  );
};

export default EditItemModal;
